import { useMemo, useState } from "react";

import {
  confirmDriverDeliveryReport,
  overrideDriverDeliveryReport,
} from "../api/client.js";
import { Icon } from "../components/Icon.jsx";
import { DriverActionDialog } from "./DriverActionDialog.jsx";
import { notifyDriver } from "./driver-notifications.js";

const FINAL_STOP_STATUSES = new Set(["COMPLETED", "COMPLETED_WITH_ISSUE", "FAILED", "CANCELLED"]);
const COMPLETED_STOP_STATUSES = new Set(["COMPLETED", "COMPLETED_WITH_ISSUE"]);
const FAILED_STOP_STATUSES = new Set(["FAILED", "CANCELLED"]);

const FAILURE_LABELS = {
  STORE_CLOSED: "Toko tutup",
  CUSTOMER_UNAVAILABLE: "Pelanggan tidak berada di lokasi",
  CUSTOMER_CANCELLED: "Pelanggan membatalkan",
  SALES_CANCELLED: "Sales membatalkan",
  CUSTOMER_REJECTED: "Pelanggan menolak barang",
  ADDRESS_NOT_FOUND: "Alamat tidak ditemukan",
  ACCESS_BLOCKED: "Kendaraan tidak dapat mengakses lokasi",
  DOCUMENT_MISMATCH: "Dokumen tidak sesuai",
  GOODS_PROBLEM: "Barang bermasalah",
  RESCHEDULE_REQUESTED: "Permintaan dijadwalkan ulang",
  VEHICLE_PROBLEM: "Kendala kendaraan",
  WEATHER: "Kendala cuaca",
  ROAD_PROBLEM: "Kendala jalan",
  OTHER: "Kendala lainnya",
};

function normalizedStatus(stop) {
  return String(stop?.status || "PENDING").toUpperCase();
}

function jakartaDate(value = new Date()) {
  const date = new Date(value);
  return new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(Number.isNaN(date.getTime()) ? new Date() : date);
}

function jakartaTime(value = new Date()) {
  const date = new Date(value);
  return new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(Number.isNaN(date.getTime()) ? new Date() : date);
}

function timeLabel(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return jakartaTime(date);
}

function stageTitle(stage) {
  if (stage === "MORNING") return "Laporan pagi";
  if (stage === "END_OF_DAY") return "Laporan akhir hari";
  if (stage === "FINAL_TRIP") return "Laporan akhir perjalanan";
  return "Bagikan status pengiriman";
}

function stageHeading(stage, { morningRevision = false } = {}) {
  if (stage === "MORNING") {
    return morningRevision
      ? "PEMBARUAN TARGET PENGIRIMAN"
      : "LAPORAN PENGIRIMAN — PAGI";
  }
  if (stage === "END_OF_DAY") return "LAPORAN PENGIRIMAN — AKHIR HARI";
  if (stage === "FINAL_TRIP") return "LAPORAN AKHIR PERJALANAN";
  return "PEMBARUAN STATUS PENGIRIMAN";
}

function reportStatusSymbol(stop, targetKeys, stage) {
  const status = normalizedStatus(stop);
  if (COMPLETED_STOP_STATUSES.has(status)) return "✅";
  if (FAILED_STOP_STATUSES.has(status)) return "❌";
  if (status === "DEFERRED") return "⏸️";
  if (status === "EN_ROUTE") return "🚚";
  if (stage === "MORNING" && targetKeys.has(String(stop.key))) return "🚚";
  return "📍";
}

function stopReason(stop, targetKeys, stage) {
  const status = normalizedStatus(stop);
  if (stage === "MORNING" && targetKeys.has(String(stop.key))) return "";
  if (!new Set(["FAILED", "CANCELLED", "DEFERRED"]).has(status)) return "";
  return stop.notes || FAILURE_LABELS[String(stop.failureReason || "").toUpperCase()] || "Alasan belum dicatat";
}

function reportSnapshot(stops) {
  return stops.map((stop) => ({
    stopKey: stop.key,
    sequence: stop.sequence,
    customerName: stop.customerName,
    status: stop.status,
    failureReason: stop.failureReason || null,
    followUpCode: stop.followUpCode || null,
    notes: stop.notes || null,
    startedAt: stop.startedAt || null,
    completedAt: stop.completedAt || null,
  }));
}

function sequenceLabel(value, total) {
  const width = Math.max(2, String(Math.max(1, Number(total || 0))).length);
  return String(Number(value || 0)).padStart(width, "0");
}

export function buildDriverDeliveryReportModel({
  detail,
  stage,
  targetStopKeys,
  generatedAt = new Date(),
}) {
  const { task, stops = [], operations, deliveryReporting } = detail;
  const targetKeys = new Set((targetStopKeys || []).map(String));
  const morningRevision = stage === "MORNING" && Boolean(deliveryReporting?.morning?.satisfied);
  const completed = stops.filter((stop) => COMPLETED_STOP_STATUSES.has(normalizedStatus(stop)));
  const failed = stops.filter((stop) => FAILED_STOP_STATUSES.has(normalizedStatus(stop)));
  const deferred = stops.filter((stop) => normalizedStatus(stop) === "DEFERRED");
  const remaining = stops.filter((stop) => !FINAL_STOP_STATUSES.has(normalizedStatus(stop)));
  const activeStop = stops.find((stop) => normalizedStatus(stop) === "EN_ROUTE") || null;
  const activeSessionStart = operations?.activeSession?.startedAt;
  const completedToday = activeSessionStart
    ? completed.filter((stop) => stop.completedAt && new Date(stop.completedAt) >= new Date(activeSessionStart))
    : [];
  const day = Number(deliveryReporting?.currentDay || operations?.tripDayCount || 1);
  const targetCount = stops.filter((stop) => targetKeys.has(String(stop.key)) && !FINAL_STOP_STATUSES.has(normalizedStatus(stop))).length;
  const durationDays = Math.max(1, Number(operations?.tripDayCount || day));
  const heading = stageHeading(stage, { morningRevision });
  const dateLine = stage === "STATUS_UPDATE"
    ? `${jakartaDate(generatedAt)} · ${jakartaTime(generatedAt)}`
    : jakartaDate(generatedAt);

  const metadata = [
    { label: "Hari perjalanan", value: String(day) },
    { label: "Fleet Order", value: task.foNumber || "—" },
    { label: "Driver", value: task.driverName || "—" },
    { label: "Co-driver", value: task.coDriverName || "Tanpa co-driver" },
    { label: "Rute", value: task.routeName || "—" },
    { label: "Kendaraan", value: task.vehiclePlate || "—" },
  ];

  const summary = stage === "MORNING"
    ? [
        { icon: "✅", label: "Selesai", value: `${completed.length} dari ${stops.length} titik` },
        { icon: "🚚", label: "Target hari ini", value: `${targetCount} titik` },
        { icon: "📍", label: "Tersisa", value: `${remaining.length} titik` },
      ]
    : stage === "END_OF_DAY"
      ? [
          { icon: "✅", label: "Selesai hari ini", value: `${completedToday.length} titik` },
          { icon: "✅", label: "Total selesai", value: `${completed.length} dari ${stops.length} titik` },
          { icon: "⏸️", label: "Gagal/ditunda", value: `${failed.length + deferred.length} titik` },
          { icon: "📍", label: "Tersisa", value: `${remaining.length} titik` },
        ]
      : stage === "FINAL_TRIP"
        ? [
            { icon: "✅", label: "Selesai", value: `${completed.length} dari ${stops.length} titik` },
            { icon: "❌", label: "Gagal/dibatalkan", value: `${failed.length} titik` },
            { icon: "🗓️", label: "Durasi perjalanan", value: `${durationDays} hari` },
          ]
        : [
            { icon: "✅", label: "Selesai", value: `${completed.length} dari ${stops.length} titik` },
            ...(activeStop
              ? [{ icon: "🚚", label: "Sedang dituju", value: activeStop.customerName }]
              : []),
            { icon: "📍", label: "Tersisa", value: `${remaining.length} titik` },
          ];

  const statusRows = stops.map((stop) => {
    const reason = stopReason(stop, targetKeys, stage);
    return {
      key: String(stop.key),
      sequence: sequenceLabel(stop.sequence, stops.length),
      symbol: reportStatusSymbol(stop, targetKeys, stage),
      customerName: stop.customerName,
      reason,
    };
  });

  return {
    stage,
    heading,
    dateLine,
    metadata,
    summary,
    statusTitle: stage === "FINAL_TRIP" ? "Status akhir" : "Status pengiriman",
    statusRows,
    finalMessage: stage === "FINAL_TRIP" ? "PERJALANAN SELESAI" : null,
  };
}

export function buildDriverDeliveryReportText(args) {
  const model = buildDriverDeliveryReportModel(args);
  return [
    `*${model.heading}*`,
    "",
    model.dateLine,
    ...model.metadata.map((item) => `${item.label}: ${item.value}`),
    "",
    "*Ringkasan*",
    ...model.summary.map((item) => `${item.icon} ${item.label}: ${item.value}`),
    "",
    `*${model.statusTitle}*`,
    ...model.statusRows.map((row) => `${row.sequence}. ${row.symbol} ${row.customerName}${row.reason ? ` — ${row.reason}` : ""}`),
    ...(model.finalMessage ? ["", `*${model.finalMessage}*`] : []),
  ].join("\n");
}

function statusDescription(state) {
  if (state?.stale) return "Perlu diperbarui setelah status pengiriman berubah";
  if (!state?.satisfied) return "Belum dibagikan";
  if (state.pendingSync) return "Dikonfirmasi · menunggu sinkronisasi";
  if (state.overridden) return "Dilanjutkan dengan alasan darurat";
  const confirmed = timeLabel(state.report?.confirmedAt);
  return confirmed ? `Sudah dibagikan · ${confirmed}` : "Sudah dibagikan";
}

async function copyReportText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("COPY_NOT_AVAILABLE");
}

function ReportPreview({ model, onCopy }) {
  return (
    <section className="driver-report-preview driver-report-visual-preview">
      <div className="driver-report-preview-heading">
        <strong>Preview laporan</strong>
        <button className="driver-report-compact-button" onClick={onCopy} type="button">
          <Icon name="document" size={16} /> Salin teks
        </button>
      </div>
      <article className="driver-report-preview-card">
        <header>
          <h3>{model.heading}</h3>
          <p>{model.dateLine}</p>
        </header>
        <dl className="driver-report-preview-meta">
          {model.metadata.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
        </dl>
        <section className="driver-report-preview-summary">
          <h4>Ringkasan</h4>
          {model.summary.map((item) => <p key={`${item.icon}:${item.label}`}><span>{item.icon}</span><strong>{item.label}</strong><span>{item.value}</span></p>)}
        </section>
        <section className="driver-report-preview-stops">
          <h4>{model.statusTitle}</h4>
          {model.statusRows.map((row) => (
            <p key={row.key}>
              <span className="driver-report-preview-sequence">{row.sequence}.</span>
              <span aria-hidden="true">{row.symbol}</span>
              <span>{row.customerName}{row.reason ? <small> — {row.reason}</small> : null}</span>
            </p>
          ))}
        </section>
        {model.finalMessage && <strong className="driver-report-preview-final">{model.finalMessage}</strong>}
      </article>
    </section>
  );
}

export function DriverDeliveryReportWizard({
  detail,
  initialStage,
  onClose,
  onSaved = async () => {},
}) {
  const reporting = detail.deliveryReporting;
  const remainingStops = useMemo(
    () => detail.stops.filter((stop) => !FINAL_STOP_STATUSES.has(normalizedStatus(stop))),
    [detail.stops],
  );
  const initialTargets = useMemo(() => {
    const previous = new Set((reporting?.morningTargetStopKeys || []).map(String));
    const matching = remainingStops.filter((stop) => previous.has(String(stop.key))).map((stop) => String(stop.key));
    return matching.length > 0 ? matching : remainingStops.map((stop) => String(stop.key));
  }, [remainingStops, reporting?.morningTargetStopKeys]);
  const [stage] = useState(initialStage);
  const [step, setStep] = useState(initialStage === "MORNING" ? "TARGET" : "PREVIEW");
  const [generatedAt, setGeneratedAt] = useState(() => new Date());
  const [targetStopKeys, setTargetStopKeys] = useState(initialTargets);
  const [manualTargets, setManualTargets] = useState(false);
  const [shared, setShared] = useState(false);
  const [sharedText, setSharedText] = useState("");
  const [shareMethod, setShareMethod] = useState(null);
  const [shareStartedAt, setShareStartedAt] = useState(null);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isStatusUpdate = stage === "STATUS_UPDATE";
  const targetValid = stage !== "MORNING" || targetStopKeys.length > 0;
  const model = useMemo(() => buildDriverDeliveryReportModel({
    detail,
    stage,
    targetStopKeys,
    generatedAt,
  }), [detail, generatedAt, stage, targetStopKeys]);
  const reportText = useMemo(() => buildDriverDeliveryReportText({
    detail,
    stage,
    targetStopKeys,
    generatedAt,
  }), [detail, generatedAt, stage, targetStopKeys]);

  function setRangeTarget(endKey) {
    const endIndex = remainingStops.findIndex((stop) => String(stop.key) === String(endKey));
    if (endIndex < 0) return;
    setTargetStopKeys(remainingStops.slice(0, endIndex + 1).map((stop) => String(stop.key)));
  }

  function freshReport() {
    const now = new Date();
    setGeneratedAt(now);
    return {
      now,
      text: buildDriverDeliveryReportText({ detail, stage, targetStopKeys, generatedAt: now }),
    };
  }

  async function finishAdHocShare(message) {
    notifyDriver(message, { tone: "success" });
    onClose();
  }

  async function copyReport() {
    setError("");
    try {
      const fresh = freshReport();
      await copyReportText(fresh.text);
      if (isStatusUpdate) {
        await finishAdHocShare("Pembaruan status disalin dan siap dikirim.");
        return;
      }
      setSharedText(fresh.text);
      setShared(true);
      setShareMethod("CLIPBOARD");
      setShareStartedAt(fresh.now.toISOString());
      notifyDriver("Teks laporan disalin. Kirim ke tujuan yang diperlukan.", { tone: "success" });
    } catch {
      setError("Teks belum dapat disalin. Gunakan tombol Bagikan laporan.");
    }
  }

  async function shareReport() {
    setError("");
    const fresh = freshReport();
    if (navigator.share) {
      try {
        await navigator.share({ title: stageTitle(stage), text: fresh.text });
        if (isStatusUpdate) {
          await finishAdHocShare("Pembaruan status berhasil dibagikan.");
          return;
        }
        setSharedText(fresh.text);
        setShared(true);
        setShareMethod("WEB_SHARE");
        setShareStartedAt(fresh.now.toISOString());
        return;
      } catch (shareError) {
        if (shareError?.name === "AbortError") return;
      }
    }
    await copyReport();
  }

  async function confirmShared() {
    setBusy(true);
    setError("");
    try {
      const result = await confirmDriverDeliveryReport(detail.task.id, {
        stage,
        operationalDay: reporting.currentDay,
        reporterType: "SELF",
        reporterName: detail.task.driverName || "Driver",
        reportText: sharedText || reportText,
        targetStopKeys: stage === "MORNING" ? targetStopKeys : (reporting.morningTargetStopKeys || []),
        stopStatusSnapshot: reportSnapshot(detail.stops),
        shareMethod: shareMethod || "OTHER",
        shareStartedAt,
        confirmedAt: new Date().toISOString(),
      });
      notifyDriver(
        result?.queued
          ? "Konfirmasi laporan tersimpan di perangkat dan akan disinkronkan."
          : "Laporan perjalanan berhasil dikonfirmasi.",
        { tone: result?.queued ? "warning" : "success" },
      );
      await onSaved({ scrollTop: false });
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Laporan belum dapat dikonfirmasi.");
    } finally {
      setBusy(false);
    }
  }

  async function saveOverride() {
    setBusy(true);
    setError("");
    try {
      const result = await overrideDriverDeliveryReport(detail.task.id, {
        stage,
        operationalDay: reporting.currentDay,
        reporterType: "SELF",
        reporterName: detail.task.driverName || "Driver",
        reason: overrideReason,
      });
      notifyDriver(
        result?.queued
          ? "Alasan darurat tersimpan di perangkat dan akan disinkronkan."
          : "Perjalanan dapat dilanjutkan dengan catatan audit.",
        { tone: "warning", duration: 7000 },
      );
      await onSaved({ scrollTop: false });
      onClose();
    } catch (requestError) {
      setError(requestError.message || "Override laporan belum dapat disimpan.");
    } finally {
      setBusy(false);
    }
  }

  const targetEndKey = targetStopKeys[targetStopKeys.length - 1] || "";
  const targetCount = remainingStops.filter((stop) => targetStopKeys.includes(String(stop.key))).length;
  const actions = overrideMode
    ? <>
        <button className="driver-secondary-button" disabled={busy} onClick={() => setOverrideMode(false)} type="button">Kembali</button>
        <button className="driver-danger-button" disabled={busy || overrideReason.trim().length < 5} onClick={saveOverride} type="button">Lanjut tanpa laporan</button>
      </>
    : step === "TARGET"
      ? <>
          <button className="driver-secondary-button" disabled={busy} onClick={onClose} type="button">Batal</button>
          <button className="driver-primary-button" disabled={busy || !targetValid} onClick={() => { setGeneratedAt(new Date()); setStep("PREVIEW"); }} type="button">Lanjut ke preview</button>
        </>
      : shared && !isStatusUpdate
        ? <>
            <button className="driver-secondary-button" disabled={busy} onClick={shareReport} type="button">Bagikan ulang</button>
            <button className="driver-primary-button" disabled={busy || !targetValid} onClick={confirmShared} type="button">Sudah dibagikan</button>
          </>
        : <>
            <button className="driver-secondary-button" disabled={busy} onClick={() => stage === "MORNING" ? setStep("TARGET") : onClose()} type="button">{stage === "MORNING" ? "Kembali" : "Batal"}</button>
            <button className="driver-primary-button" disabled={busy || !targetValid} onClick={shareReport} type="button"><Icon name="upload" size={17} /> {isStatusUpdate ? "Bagikan pembaruan" : "Bagikan laporan"}</button>
          </>;

  return (
    <DriverActionDialog
      actions={actions}
      busy={busy}
      description={`Hari ${reporting.currentDay} · ${detail.task.foNumber}`}
      onClose={onClose}
      title={stageTitle(stage)}
    >
      {error && <div className="driver-error-banner">{error}</div>}
      <div className="driver-report-wizard">
        {overrideMode ? (
          <label><span>Alasan darurat</span><textarea minLength="5" onChange={(event) => setOverrideReason(event.target.value)} placeholder="Contoh: WhatsApp tidak dapat dibuka dan pengiriman harus segera berjalan" rows="3" value={overrideReason} /></label>
        ) : step === "TARGET" ? (
          <section className="driver-report-targets driver-report-target-step">
            <div>
              <span className="driver-report-step-label">Langkah 1 dari 2</span>
              <strong>Target pengiriman hari ini</strong>
              <p>Default mengikuti urutan Fleet Order dan dapat disesuaikan bila diperlukan.</p>
            </div>
            {!manualTargets && remainingStops.length > 0 && (
              <label>
                <span>Target sampai titik</span>
                <select onChange={(event) => setRangeTarget(event.target.value)} value={targetEndKey}>
                  {remainingStops.map((stop) => <option key={stop.key} value={stop.key}>{stop.sequence}. {stop.customerName}</option>)}
                </select>
              </label>
            )}
            <p className="driver-report-target-count"><strong>{targetCount} titik</strong> yang belum selesai akan ditargetkan hari ini.</p>
            <button className="driver-report-secondary-action" onClick={() => setManualTargets((value) => !value)} type="button">
              {manualTargets ? "Gunakan target berurutan" : "Sesuaikan satu per satu"}
            </button>
            {manualTargets && <div className="driver-report-target-list">{remainingStops.map((stop) => <label key={stop.key}><input checked={targetStopKeys.includes(String(stop.key))} onChange={(event) => setTargetStopKeys((current) => event.target.checked ? [...new Set([...current, String(stop.key)])] : current.filter((key) => key !== String(stop.key)))} type="checkbox" /><span>{sequenceLabel(stop.sequence, detail.stops.length)}. {stop.customerName}</span></label>)}</div>}
          </section>
        ) : (
          <>
            <span className="driver-report-step-label">{stage === "MORNING" ? "Langkah 2 dari 2" : "Siap dibagikan"}</span>
            <ReportPreview model={model} onCopy={copyReport} />
            {shared && !isStatusUpdate && <div className="driver-info-note">Pastikan laporan sudah dibagikan ke tujuan yang benar, lalu tekan konfirmasi di bawah.</div>}
            {!isStatusUpdate && <button className="driver-report-override-link" onClick={() => setOverrideMode(true)} type="button">Lanjut tanpa laporan dalam keadaan darurat</button>}
          </>
        )}
      </div>
    </DriverActionDialog>
  );
}

export function DriverDeliveryReportPanel({ detail, onChanged }) {
  const reporting = detail.deliveryReporting;
  const [wizardStage, setWizardStage] = useState(null);
  if (!reporting?.available) return null;

  const morningAction = reporting.morning.satisfied ? "Ubah target laporan pagi" : "Siapkan laporan pagi";
  const closingStage = reporting.allStopsFinal ? "FINAL_TRIP" : "END_OF_DAY";
  const closingState = reporting.allStopsFinal ? reporting.finalTrip : reporting.endOfDay;
  const closingAction = closingState.stale
    ? (reporting.allStopsFinal ? "Perbarui laporan akhir perjalanan" : "Perbarui laporan akhir hari")
    : (reporting.allStopsFinal ? "Siapkan laporan akhir perjalanan" : "Siapkan laporan akhir hari");
  const canPrepareClosing = reporting.allStopsFinal
    ? reporting.canPrepareFinalTrip
    : reporting.canPrepareEndOfDay;
  const reportingActive = new Set(["PRE_START", "OPEN"]).has(reporting.currentSessionStatus);

  return (
    <div className="driver-delivery-report-panel">
      <div className="driver-delivery-report-heading">
        <div><span className="driver-eyebrow">Laporan perjalanan</span><strong>Status laporan wajib</strong></div>
        <span>Hari {reporting.currentDay}</span>
      </div>
      <div className="driver-delivery-report-statuses">
        <article className={reporting.morning.satisfied ? "is-complete" : "is-required"}>
          <Icon name={reporting.morning.satisfied ? "check" : "clock"} size={17} />
          <div><strong>Laporan pagi</strong><small>{statusDescription(reporting.morning)}</small></div>
        </article>
        {reporting.currentSessionStatus === "OPEN" && (
          <article className={closingState.satisfied ? "is-complete" : closingState.stale ? "is-required" : ""}>
            <Icon name={closingState.satisfied ? "check" : "document"} size={17} />
            <div><strong>{reporting.allStopsFinal ? "Laporan akhir perjalanan" : "Laporan akhir hari"}</strong><small>{statusDescription(closingState)}</small></div>
          </article>
        )}
      </div>
      {reportingActive && (
        <div className="driver-delivery-report-actions">
          {!reporting.allStopsFinal && <button className={reporting.morning.satisfied ? "driver-secondary-button" : "driver-primary-button"} onClick={() => setWizardStage("MORNING")} type="button">{morningAction}</button>}
          {reporting.currentSessionStatus === "OPEN" && !closingState.satisfied && canPrepareClosing && <button className={reporting.allStopsFinal ? "driver-primary-button" : "driver-secondary-button"} onClick={() => setWizardStage(closingStage)} type="button">{closingAction}</button>}
        </div>
      )}
      {reporting.currentSessionStatus === "OPEN" && !reporting.allStopsFinal && !reporting.morning.satisfied && (
        <p className="driver-report-panel-hint">Selesaikan laporan pagi sebelum membuat laporan akhir hari.</p>
      )}
      {reporting.currentSessionStatus === "OPEN" && reporting.activeStopCount > 0 && (
        <p className="driver-report-panel-hint">Selesaikan atau tunda titik aktif sebelum membuat laporan penutupan.</p>
      )}
      {wizardStage && <DriverDeliveryReportWizard detail={detail} initialStage={wizardStage} onClose={() => setWizardStage(null)} onSaved={onChanged} />}
    </div>
  );
}
